// Saved filters tab body — Polish B2 + visibility upgrade.

import { Loader2, Pencil, Trash2, Users } from "lucide-react";
import { useMemo, useState } from "react";

import { useAuth } from "../../auth/AuthContext";
import {
  useDeleteSavedFilterMutation,
  useSavedFiltersQuery,
} from "../hooks";
import type { SavedFilterDto } from "../api";


export interface SavedFiltersTabProps {
  onApply: (filter: SavedFilterDto) => void;
  onEdit: (filter: SavedFilterDto) => void;
}


export function SavedFiltersTab({ onApply, onEdit }: SavedFiltersTabProps) {
  const query = useSavedFiltersQuery("commitment");
  const deleteMutation = useDeleteSavedFilterMutation();
  const { user } = useAuth();
  const [search, setSearch] = useState("");

  const rows = query.data?.rows ?? [];
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle.length === 0) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(needle));
  }, [rows, search]);

  const currentUserId = user?.id ?? null;

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <input
          type="search"
          data-testid="commitments-saved-filters-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search saved filters…"
          className="w-full rounded-md border border-border-subtle bg-raised px-3 py-1.5 text-xs text-text-primary"
        />
      </div>
      {query.isLoading ? (
        <div className="flex items-center gap-1 text-xs text-text-tertiary">
          <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          Loading saved filters…
        </div>
      ) : filtered.length === 0 ? (
        <p
          data-testid="commitments-saved-filters-empty"
          className="rounded-md bg-recessed/40 px-3 py-2 text-xs text-text-tertiary"
        >
          {rows.length === 0
            ? "No saved filters yet. Build one in the New Filter tab and click Save."
            : "No saved filters match that search."}
        </p>
      ) : (
        <ul role="list" className="flex flex-col gap-1">
          {filtered.map((sf) => {
            const isShared = sf.visibility === "broker";
            // Legacy rows with no created_by attribution fall back
            // to the read gate (personal owner / anyone for broker
            // rows) so old filters stay editable instead of getting
            // stuck. Backend mirrors this.
            const canModify = sf.created_by_id != null
              ? sf.created_by_id === currentUserId
              : (sf.user_id == null || sf.user_id === currentUserId);
            return (
              <li key={sf.id}>
                <div
                  data-testid={`commitments-saved-filter-row-${sf.id}`}
                  className="flex items-center gap-1 rounded-md hover:bg-recessed/40"
                >
                  <button
                    type="button"
                    data-testid={`commitments-saved-filter-apply-${sf.id}`}
                    onClick={() => onApply(sf)}
                    className="flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text-primary"
                  >
                    <span className="truncate">{sf.name}</span>
                    {isShared && (
                      <span
                        data-testid={`commitments-saved-filter-shared-${sf.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-teal/15 px-1.5 py-0.5 text-[10px] font-medium text-teal"
                        title="Visible to your whole broker"
                      >
                        <Users size={10} aria-hidden="true" />
                        Shared
                      </span>
                    )}
                  </button>
                  {canModify && (
                    <button
                      type="button"
                      data-testid={`commitments-saved-filter-edit-${sf.id}`}
                      aria-label={`Edit ${sf.name}`}
                      onClick={() => onEdit(sf)}
                      className="rounded p-1 text-text-tertiary hover:text-text-primary"
                    >
                      <Pencil size={14} aria-hidden="true" />
                    </button>
                  )}
                  {canModify && (
                    <button
                      type="button"
                      data-testid={`commitments-saved-filter-delete-${sf.id}`}
                      aria-label={`Delete ${sf.name}`}
                      onClick={() => {
                        if (
                          window.confirm(`Delete saved filter "${sf.name}"?`)
                        ) {
                          deleteMutation.mutate(sf.id);
                        }
                      }}
                      className="rounded p-1 text-ember/70 hover:text-ember"
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
